import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { palette } from "../../styles/theme";
import { SpecsAssetImage } from "./SpecsEditorFields/index";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { getElementSelectionKey } from "./positionsLib";
import { getAssetPreviewUrl } from "./specsEditorUtils";
import { logSpecsModelPath } from "./specsModelPathLogger";
import type { ElementModel } from "./specsModel/ElementModel";
import type { PartModel } from "./specsModel/PartModel";

function generateTooltipTitle(
  part: PartModel,
  element: ElementModel,
  isPossibleInPositionTab: boolean
) {
  const title = `${part.name} / ${element.name}`;

  if (!isPossibleInPositionTab) {
    return `${title} is not available for this position`;
  }

  return title;
}

export default function SpecsEditorLeftSidebar() {
  const {
    editor,
    currentVersion,
    nav,
    selectedPositionId,
    selectedElement,
    assignedElementKeysForSelectedPosition,
  } = useSpecsEditorContextValue();
  const parts = React.useMemo(
    () => currentVersion?.parts ?? [],
    [currentVersion]
  );

  React.useEffect(() => {
    const firstElement = parts
      .flatMap((part) =>
        part.elements.map((element) => ({
          part,
          element,
        }))
      )
      .find(({ element }) => element.styles.length > 0);

    if (nav.kind !== "layer" || selectedElement || !firstElement) {
      return;
    }

    const firstStyleName =
      firstElement.element.defaultStyle ?? firstElement.element.styles[0]?.name;

    if (!firstStyleName) {
      return;
    }

    editor.selectElement(
      firstElement.part.name,
      firstElement.element.name,
      firstStyleName
    );
  }, [editor, nav.kind, parts, selectedElement]);

  const selectedElementKey = selectedElement
    ? getElementSelectionKey(
        selectedElement.part.name,
        selectedElement.element.name
      )
    : null;

  return (
    <Box
      sx={{
        width: 72,
        minWidth: 72,
        maxHeight: "100vh",
        overflowY: "auto",
        borderRight: `1px solid ${palette.grey.lightGrey}`,
        bgcolor: palette.grey.lightBackground,
        py: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.25,
      }}
    >
      {parts.map((part) => {
        const partElements = part.elements;

        if (partElements.length === 0) {
          return null;
        }

        return (
          <Stack
            key={part.name}
            alignItems="center"
            spacing={0.25}
            sx={{ width: "100%" }}
          >
            <Tooltip title={`Part: ${part.name}`} placement="right">
              <Typography
                variant="caption"
                sx={{
                  width: 56,
                  px: 0.5,
                  color: "text.secondary",
                  fontSize: "9px",
                  fontWeight: 700,
                  lineHeight: 1.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "center",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                {part.name}
              </Typography>
            </Tooltip>

            {partElements.map((element) => {
              const elementKey = getElementSelectionKey(
                part.name,
                element.name
              );
              const isPossibleInPositionTab =
                nav.kind !== "positions" ||
                !selectedPositionId ||
                assignedElementKeysForSelectedPosition === null ||
                assignedElementKeysForSelectedPosition.has(elementKey);
              const firstAssignedStyleName =
                editor.getFirstAssignedStyleNameForElement(part, element);

              return (
                <Tooltip
                  key={elementKey}
                  title={generateTooltipTitle(
                    part,
                    element,
                    isPossibleInPositionTab
                  )}
                  placement="right"
                >
                  <Box
                    onClick={() => {
                      if (!isPossibleInPositionTab || !firstAssignedStyleName) {
                        return;
                      }

                      logSpecsModelPath(element._debugPath);
                      editor.selectElement(
                        part.name,
                        element.name,
                        firstAssignedStyleName
                      );
                    }}
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "6px",
                      cursor: isPossibleInPositionTab
                        ? "pointer"
                        : "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border:
                        selectedElementKey === elementKey
                          ? `2px solid ${palette.brand.primary}`
                          : "2px solid transparent",
                      bgcolor:
                        selectedElementKey === elementKey
                          ? palette.brand.paleEffect
                          : "transparent",
                      opacity: isPossibleInPositionTab ? 1 : 0.35,
                      filter: isPossibleInPositionTab ? "none" : "grayscale(1)",
                      "&:hover": {
                        bgcolor: isPossibleInPositionTab
                          ? palette.brand.paleEffect
                          : "transparent",
                      },
                    }}
                  >
                    {element.menuIconUrl ? (
                      <SpecsAssetImage
                        src={getAssetPreviewUrl(
                          element.displayExtra,
                          "menuIconUrl",
                          element.menuIconUrl
                        )}
                        alt={element.name}
                        sx={{ width: 36, height: 36, objectFit: "contain" }}
                      />
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "9px", textAlign: "center" }}
                      >
                        {element.name}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        );
      })}
    </Box>
  );
}
